<template>
    <div
        :class="{ 'draft-sale': property.transaction_type === 'sale' && isDraft, 'draft-let': property.transaction_type === 'let' && isDraft }"
        class="property rounded-corners"
    >
        <div class="row no-gutters">
            <!-- Property Image -->
            <div class="col-12 col-lg-5 col-xl-6">
                <router-link
                    :to="{ name: 'view-property', params: { id: property.id } }"
                    class="property__link"
                >
                    <div class="property__image">
                        <div v-if="property.assets && property.assets.gallery_image && property.assets.gallery_image.length">
                            <img :src="property.assets.gallery_image[0].src">
                        </div>
                        <span
                            :class="{
                                'label-tag--sell': property.transaction_type === 'sale',
                                'label-tag--let': property.transaction_type === 'let'
                            }"
                            class="property__image-tag label-tag"
                        >
                            {{ getTransactionType(property.transaction_type) }}
                        </span>
                    </div>
                </router-link>
            </div>

            <!-- Property Content -->
            <div class="col-12 col-lg-7 col-xl-6">
                <div class="property__content">
                    <div>
                        <router-link
                            :to="{ name: linkToEdit || property.status.name !== 'available' ? 'property' : 'view-property', params: { id: property.id } }"
                            class="property__link"
                        >
                            <h4 class="property__content-title">
                                {{ property.full_address || 'New Property' }}
                            </h4>

                            <div class="my-4 pb-2">
                                <p class="mb-0">
                                    {{ property.price_prefix }}
                                </p>
                                <p class="h2 color-secondary text-bold mb-4">
                                    {{ property.price | price(false) }}
                                </p>
                                <p
                                    v-if="owned"
                                    :class="{
                                        'color-secondary': property.status.name === 'available'
                                    }"
                                    class="text-bold"
                                >
                                    {{ property.status.display_name }}
                                </p>
                            </div>
                        </router-link>
                    </div>
                    <p
                        v-if="!property.full_address"
                        class="mb-4"
                    >
                        Edit this property to add details including the address, description and more...
                    </p>
                    <div v-if="!owned">
                        <div class="property__content-details">
                            <div class="mr-4">
                                <p class="text-bold p-sm">
                                    Bedrooms
                                </p>
                                <p class="p-sm">
                                    {{ property.bedrooms }}
                                </p>
                            </div>
                            <div>
                                <p class="text-bold p-sm">
                                    Bathrooms
                                </p>
                                <p class="p-sm">
                                    {{ property.bathrooms }}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="icons.notifications || icons.messages"
                        class="d-flex justify-content-between align-items-center property__content-bottom py-4"
                    >
                        <div>
                            <span
                                v-if="hasOffer"
                                class="label-type label-type--sell"
                            >
                                Has Offer
                            </span>
                        </div>
                        <ul class="list-unstyled mb-0 mt-auto d-flex justify-content-end property__content-icons">
                            <li v-if="icons.notifications">
                                <router-link
                                    :to="{ name: 'notifications' }"
                                    class="property__content-icon"
                                >
                                    <SVGSprite name="bell" />
                                    <span
                                        v-if="notifications.length"
                                        class="item-alert"
                                    >
                                        {{ notifications.length }}
                                    </span>
                                </router-link>
                            </li>
                            <li v-if="icons.messages">
                                <router-link
                                    :to="{ name: 'messages' }"
                                    class="property__content-icon"
                                >
                                    <SVGSprite name="messages" />
                                    <span
                                        v-if="property.unread_messages"
                                        class="item-alert"
                                    >
                                        {{ property.unread_messages }}
                                    </span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                    <div class="d-flex justify-content-between align-items-center property__content-bottom pt-4">
                        <div v-if="icons.save">
                            <a
                                :class="{ 'property__content-icon--active': isBookmarked }"
                                href="#"
                                class="property__content-icon"
                                @click.prevent="toggleBookmark(property.id)"
                            >
                                <SVGSprite name="heart" />
                            </a>
                        </div>
                        <div v-if="icons.view && property.status.name === 'available'">
                            <router-link
                                :to="{ name: 'view-property', params: { id: property.id } }"
                                class="btn btn--sm btn--outline btn--nomin"
                            >
                                View
                            </router-link>
                        </div>
                        <div v-if="icons.edit" />
                        <div v-if="icons.edit">
                            <router-link
                                :to="{ name: 'property', params: { id: property.id } }"
                                class="btn btn--sm btn--outline btn--nomin"
                            >
                                Edit
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';

export default {
    name: 'PropertyCard',
    props: {
        property: {
            type: Object,
            required: true
        },
        owned: {
            type: Boolean,
            default: false
        },
        icons: {
            type: Object,
            default() {
                return {
                    save: false,
                    notifications: false,
                    messages: false,
                    edit: false,
                    view: false
                };
            }
        },
        linkToEdit: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            transactionTypes: {
                sale: 'For Sale',
                let: 'To Let'
            }
        };
    },
    computed: {
        ...mapState('user', ['user']),
        isBookmarked() {
            return this.user.bookmarks
                && this.user.bookmarks.length
                && this.user.bookmarks.find(bookmark => this.property.id === bookmark.id);
        },
        notifications() {
            return (this.user.notifications
                && this.user.notifications.length
                && this.user.notifications.filter(notification => notification.target_type === 'property' && notification.target_id === this.property.id)
            ) || [];
        },
        hasOffer() {
            return this.notifications.filter(notification => notification.type === 'offer_made').length;
        },
        isDraft() {
            return this.property.status.name === 'draft';
        }
    },
    methods: {
        ...mapActions('user', ['toggleBookmark']),
        getTransactionType(type) {
            return this.transactionTypes[type] || null;
        }
    }
};
</script>

<style lang="scss">
.property {
    @include media-breakpoint-up(lg) {
        height: 100%;

        .row {
            height: 100%;
        }
    }

    &.draft-sale {
        border: 1px solid theme-color('sell');
    }
    &.draft-let {
        border: 1px solid theme-color('let');
    }
}
</style>
